/**
 * mutation-store — zustand store coordinating the 4 cogo.ontology.* mutations.
 *
 * State machine:
 *   idle → pending(N) → idle(+lastResult)        — happy path
 *   idle → pending(N) → idle(+lastError)         — SHACL or transport error
 *   pending list tracks in-flight requests so multi-tool sequences (e.g.
 *   preview → merge) coexist; a failed pending item is removed before its
 *   error propagates up so the UI's "Pending..." indicator clears.
 *
 * `previewResult` is intentionally separate from `lastResult` so the
 * inspector can show the last-applied mutation alongside a proposed preview.
 *
 * Test isolation: `_resetForTest()` mirrors core-loader.ts's pattern (Phase
 * 001 precedent) — production code MUST NOT call it.
 */

import { create } from 'zustand'
import type {
  AddBlockArgs,
  AddBlockResult,
  ModifyActionArgs,
  ModifyActionResult,
  PreviewChangesArgs,
  PreviewChangesResult,
  MergeBranchArgs,
  MergeBranchResult,
  McpMutationClient,
} from './mcp-mutation-client'
import { isShaclViolationError, type ShaclViolationError } from './shacl-error'

export type ToolName = 'add_block' | 'modify_action' | 'preview_changes' | 'merge_branch'

export interface PendingMutation {
  id: string
  tool: ToolName
  startedAt: number
}

export interface AppliedResult {
  tool: ToolName
  result: AddBlockResult | ModifyActionResult | MergeBranchResult
}

export interface MutationStoreState {
  status: 'idle' | 'pending'
  pending: PendingMutation[]
  lastResult: AppliedResult | null
  lastError: ShaclViolationError | Error | null
  previewResult: PreviewChangesResult | null
  setClient(client: McpMutationClient): void
  addBlock(args: AddBlockArgs): Promise<AddBlockResult>
  modifyAction(args: ModifyActionArgs): Promise<ModifyActionResult>
  previewChanges(args: PreviewChangesArgs): Promise<PreviewChangesResult>
  mergeBranch(args: MergeBranchArgs): Promise<MergeBranchResult>
  clearPreview(): void
  /** Test-only reset; PRODUCTION CODE MUST NOT CALL THIS. */
  _resetForTest(): void
}

let _client: McpMutationClient | null = null

let _idCounter = 0
function nextId(): string {
  _idCounter += 1
  return `mut-${Date.now()}-${_idCounter}`
}

export const useMutationStore = create<MutationStoreState>((set, get) => ({
  status: 'idle',
  pending: [],
  lastResult: null,
  lastError: null,
  previewResult: null,

  setClient(client) {
    _client = client
  },

  async addBlock(args) {
    return runTool('add_block', set, () => {
      if (!_client) throw new Error('[mutation-store] client not set — call setClient first')
      return _client.addBlock(args)
    }) as Promise<AddBlockResult>
  },

  async modifyAction(args) {
    return runTool('modify_action', set, () => {
      if (!_client) throw new Error('[mutation-store] client not set — call setClient first')
      return _client.modifyAction(args)
    }) as Promise<ModifyActionResult>
  },

  async previewChanges(args) {
    const result = (await runTool('preview_changes', set, () => {
      if (!_client) throw new Error('[mutation-store] client not set — call setClient first')
      return _client.previewChanges(args)
    })) as PreviewChangesResult
    set({ previewResult: result })
    return result
  },

  async mergeBranch(args) {
    return runTool('merge_branch', set, () => {
      if (!_client) throw new Error('[mutation-store] client not set — call setClient first')
      return _client.mergeBranch(args)
    }) as Promise<MergeBranchResult>
  },

  clearPreview() {
    set({ previewResult: null })
  },

  _resetForTest() {
    _client = null
    _idCounter = 0
    set({
      status: 'idle',
      pending: [],
      lastResult: null,
      lastError: null,
      previewResult: null,
    })
  },
}))

async function runTool(
  tool: ToolName,
  set: (
    partial: Partial<MutationStoreState> | ((s: MutationStoreState) => Partial<MutationStoreState>),
  ) => void,
  fn: () => Promise<unknown>,
): Promise<unknown> {
  const id = nextId()
  set((s) => ({
    status: 'pending',
    pending: [...s.pending, { id, tool, startedAt: Date.now() }],
  }))
  try {
    const result = (await fn()) as AddBlockResult | ModifyActionResult | MergeBranchResult | PreviewChangesResult
    set((s) => ({
      pending: s.pending.filter((p) => p.id !== id),
      status: s.pending.length <= 1 ? 'idle' : 'pending',
      lastResult:
        tool === 'preview_changes'
          ? s.lastResult
          : ({ tool, result: result as AddBlockResult | ModifyActionResult | MergeBranchResult }),
      lastError: tool === 'preview_changes' ? s.lastError : null,
    }))
    return result
  } catch (e) {
    const err = isShaclViolationError(e) ? e : (e as Error)
    set((s) => ({
      pending: s.pending.filter((p) => p.id !== id),
      status: s.pending.length <= 1 ? 'idle' : 'pending',
      lastError: err,
    }))
    throw err
  }
}
